import { visibleTrash } from '../lib/trash';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { formatDateTime } from '../lib/locale';
import { BoardSearch } from './BoardSearch';
import { useEffect, useState } from 'react';
import { useStore } from 'zustand';
import { templatesStore } from '../features/templates/store';
import { Link, useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Boxes, Copy, Pencil, Plus, RotateCcw, Settings, Trash2 } from 'lucide-react';
import type { api } from '@stageplottter/core';
import { memberCan } from '@stageplottter/core';
import { getApi } from '../api/endpoints';
import { useAsync } from '../hooks/useAsync';
import { ErrorBox, errorMessageKey } from './ErrorBox';
import { toastStore } from '../state/toasts';
import { useMe } from '../shell/me';
import { CommitInput } from '../ui/CommitInput';
import { BoardImport } from '../features/importfiles/BoardImport';
import { NewBoardFromRider } from '../features/aiimport/NewBoardFromRider';
import '../features/sharing/sharing.css';

function useFail() {
  const { t } = useTranslation();
  return (err: unknown) =>
    toastStore.getState().push({ kind: 'error', message: t(errorMessageKey(err)) });
}

function BoardRow({
  board,
  canDelete,
  onChanged,
}: {
  board: api.BoardSummary;
  canDelete: boolean;
  onChanged(): void;
}) {
  const { t, i18n } = useTranslation();
  const [renaming, setRenaming] = useState(false);
  const fail = useFail();
  const navigate = useNavigate();
  const run = (p: Promise<unknown>, okKey?: string) =>
    p.then(() => {
      if (okKey) toastStore.getState().push({ kind: 'success', message: t(okKey) });
      onChanged();
    }, fail);
  return (
    <li className="board-row">
      {renaming ? (
        <CommitInput
          aria-label={t('boards.rename')}
          value={board.name}
          // eslint-disable-next-line jsx-a11y/no-autofocus -- appears in response to an explicit rename action
          autoFocus
          onBlur={() => setRenaming(false)}
          onCommit={(name) => {
            if (name.trim() === '') return false;
            void run(getApi().renameBoard(board.id, name.trim()));
            setRenaming(false);
          }}
        />
      ) : (
        <Link className="board-name" to={`/b/${board.id}`}>
          {board.name}
        </Link>
      )}
      <span className="muted small">
        {t('boards.updated', { date: formatDateTime(board.updatedAt, i18n.language) })}
      </span>
      <div className="row-actions">
        <button
          type="button"
          className="icon-btn"
          aria-label={t('boards.rename')}
          onClick={() => setRenaming(true)}
        >
          <Pencil size={15} />
        </button>
        <button
          type="button"
          className="icon-btn"
          aria-label={t('boards.clone')}
          onClick={() =>
            getApi()
              .cloneBoard(board.id, t('boards.copyName', { name: board.name }))
              .then((b) => navigate(`/b/${b.id}`), fail)
          }
        >
          <Copy size={15} />
        </button>
        {canDelete && (
          <button
            type="button"
            className="icon-btn danger"
            aria-label={t('boards.delete')}
            onClick={() => void run(getApi().deleteBoard(board.id), 'boards.movedToTrash')}
          >
            <Trash2 size={15} />
          </button>
        )}
      </div>
    </li>
  );
}

export function BoardsPage() {
  const { t, i18n } = useTranslation();
  const { wsId = '' } = useParams();
  const navigate = useNavigate();
  const me = useMe();
  const fail = useFail();
  const boards = useAsync(`boards:${wsId}`, () => getApi().listBoards(wsId));
  const trash = useAsync(`trash:${wsId}`, () => getApi().listTrash(wsId));
  const [showAllTrash, setShowAllTrash] = useState(false);
  useDocumentTitle(
    t('boards.title'),
    me.status === 'ready' ? me.me.workspaces.find((w) => w.id === wsId)?.name : undefined,
  );
  const [name, setName] = useState('');
  const [width, setWidth] = useState('10');
  const [depth, setDepth] = useState('6');
  // Workspace templates (U16): a new board can start from one of them.
  const templates = useStore(templatesStore);
  const [templateId, setTemplateId] = useState('');
  useEffect(() => {
    void templatesStore.getState().load(wsId, true);
  }, [wsId]);
  const templateList = templates.ws === wsId ? templates.list : [];
  const role =
    me.status === 'ready' ? me.me.workspaces.find((w) => w.id === wsId)?.role : undefined;
  const canCreate = role === undefined || memberCan(role, 'board.create');
  const canDelete = role === undefined || memberCan(role, 'board.delete');
  const reload = () => {
    boards.reload();
    trash.reload();
  };

  const create = () => {
    const w = Number(width.replace(',', '.'));
    const d = Number(depth.replace(',', '.'));
    void getApi()
      .createBoard(wsId, {
        name: name.trim(),
        ...(w > 0 ? { stageWidth: w } : {}),
        ...(d > 0 ? { stageDepth: d } : {}),
        ...(templateId !== '' ? { templateId } : {}),
      })
      .then((b) => navigate(`/b/${b.id}`), fail);
  };

  /** Seeds a complete example band (core demo fixture) – handy for trying things out. */
  const createDemo = () => {
    void getApi()
      .createBoard(wsId, { name: name.trim() || t('boards.demoName'), template: 'demo' })
      .then((b) => navigate(`/b/${b.id}`), fail);
  };

  return (
    <div className="page">
      <div className="page-head">
        <h1>{t('boards.title')}</h1>
        <Link className="btn btn-ghost" to={`/w/${encodeURIComponent(wsId)}/inventory`}>
          <Boxes size={15} aria-hidden="true" /> {t('inventory.link')}
        </Link>
        <Link className="btn btn-ghost" to={`/w/${encodeURIComponent(wsId)}/settings`}>
          <Settings size={15} aria-hidden="true" /> {t('boards.workspaceSettings')}
        </Link>
      </div>
      {canCreate && (
        <form
          className="inline-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim() !== '') create();
          }}
        >
          <label className="field">
            <span>{t('boards.newName')}</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              required
            />
          </label>
          <label className="field narrow">
            <span>{t('boards.stageWidth')}</span>
            <input value={width} inputMode="decimal" onChange={(e) => setWidth(e.target.value)} />
          </label>
          <label className="field narrow">
            <span>{t('boards.stageDepth')}</span>
            <input value={depth} inputMode="decimal" onChange={(e) => setDepth(e.target.value)} />
          </label>
          {templateList.length > 0 && (
            <label className="field">
              <span>{t('templates.start')}</span>
              <select value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                <option value="">{t('templates.blank')}</option>
                {templateList.map((tpl) => (
                  <option key={tpl.id} value={tpl.id}>
                    {tpl.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <button type="submit" className="btn btn-primary" disabled={name.trim() === ''}>
            <Plus size={16} /> {t('boards.create')}
          </button>
          <button type="button" className="btn" onClick={createDemo}>
            {t('boards.createDemo')}
          </button>
          <NewBoardFromRider workspaceId={wsId} name={name} />
        </form>
      )}
      {canCreate && <BoardImport workspaceId={wsId} />}
      <BoardSearch workspaceId={wsId} />
      {boards.status === 'loading' && <p className="muted">{t('common.loading')}</p>}
      {boards.status === 'error' && <ErrorBox error={boards.error} onRetry={boards.reload} />}
      {boards.status === 'ready' && (
        <>
          {boards.data.length === 0 && <p className="muted">{t('boards.empty')}</p>}
          <ul className="board-list">
            {boards.data.map((b) => (
              <BoardRow key={b.id} board={b} canDelete={canDelete} onChanged={reload} />
            ))}
          </ul>
        </>
      )}
      <h2>{t('boards.trash')}</h2>
      <p className="muted small">{t('boards.trashHint')}</p>
      {trash.status === 'error' && <ErrorBox error={trash.error} onRetry={trash.reload} />}
      {trash.status === 'ready' && trash.data.length === 0 && (
        <p className="muted">{t('boards.trashEmpty')}</p>
      )}
      {trash.status === 'ready' && (
        <ul className="board-list">
          {visibleTrash(trash.data, showAllTrash).shown.map((b) => (
            <li key={b.id} className="board-row trashed">
              <span className="board-name">{b.name}</span>
              <span className="muted small">
                {b.deletedAt
                  ? t('boards.deletedAt', {
                      date: formatDateTime(b.deletedAt, i18n.language),
                    })
                  : ''}
              </span>
              <div className="row-actions">
                <button
                  type="button"
                  className="btn"
                  onClick={() =>
                    getApi()
                      .restoreBoard(b.id)
                      .then(() => {
                        toastStore
                          .getState()
                          .push({ kind: 'success', message: t('boards.restored') });
                        reload();
                      }, fail)
                  }
                >
                  <RotateCcw size={15} /> {t('boards.restore')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {trash.status === 'ready' && visibleTrash(trash.data, showAllTrash).hidden > 0 && (
        <button type="button" className="btn btn-ghost" onClick={() => setShowAllTrash(true)}>
          {t('boards.trashShowAll', { count: trash.data.length })}
        </button>
      )}
    </div>
  );
}
